import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Badge } from '@quebon/ui/components/badge'
import {
  Building2,
  Search,
  Plus,
  Eye,
  Loader2,
  AlertCircle,
  X,
  ArrowUpDown,
  Upload,
  Image,
} from 'lucide-react'
import {
  adminApi,
  AdminOrganization,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
} from '@/api/admin'

/** 현재 로컬 날짜를 YYYY-MM-DD 포맷으로 반환 */
function getLocalDateString(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** UTC ISO 문자열에서 로컬 날짜(YYYY-MM-DD) 추출 */
function toLocalDate(isoString: string | null | undefined): string {
  if (!isoString) return ''
  const d = new Date(isoString)
  if (isNaN(d.getTime())) return isoString.split('T')[0] || ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const ORGANIZATION_TYPES = [
  { value: 'company', label: '기업' },
  { value: 'learning_center', label: 'Learning Center' },
  { value: 'academy', label: '학원' },
  { value: 'school', label: '학교' },
  { value: 'institution', label: '기관' },
  { value: 'other', label: '기타' }
]

const ORGANIZATION_TYPE_LABELS: Record<string, string> = {
  company: '기업',
  learning_center: 'LC',
  academy: '학원',
  school: '학교',
  institution: '기관',
  other: '기타'
}

const LANGUAGE_MODE_OPTIONS = [
  { value: 'ko', label: '한국어' },
  { value: 'en', label: 'English' },
  { value: 'both', label: '한국어 + English' },
]

// 조직 타입에 따른 도메인 매핑
const getFullDomain = (subdomain: string | null, organizationType: string): string | null => {
  if (!subdomain) return null
  
  if (organizationType === 'learning_center') {
    return `${subdomain}.quebonLC.tv`
  }
  // company 및 기타 타입은 aiqlab.tv
  return `${subdomain}.aiqlab.tv`
}


type SortField = 'name' | 'createdAt' | 'contractEndDate'
type SortOrder = 'asc' | 'desc'

// ============================================================================
// Organization Form Modal
// ============================================================================

interface OrganizationFormModalProps {
  organization?: AdminOrganization | null
  onClose: () => void
  onSave: (data: CreateOrganizationRequest | UpdateOrganizationRequest) => void
  isLoading: boolean
}

function OrganizationFormModal({
  organization,
  onClose,
  onSave,
  isLoading
}: OrganizationFormModalProps) {
  const [formData, setFormData] = useState<CreateOrganizationRequest>({
    name: organization?.name || '',
    displayName: organization?.displayName || '',
    representativeName: organization?.representativeName || '',
    organizationType: organization?.organizationType || 'company',
    businessCategory: organization?.businessCategory || '',
    subdomain: organization?.subdomain || '',
    email: organization?.email || '',
    phoneNumber: organization?.phoneNumber || '',
    websiteUrl: organization?.websiteUrl || '',
    logoUrl: organization?.logoUrl || '',
    primaryColor: organization?.primaryColor || '#3B82F6',
    contractStartDate: organization?.contractStartDate || '',
    contractEndDate: organization?.contractEndDate || '',
    maxSeats: organization?.maxSeats || 0,
    languageMode: organization?.languageMode || 'ko',
    lcOperationMode: organization?.lcOperationMode || 'PRODUCTION',
  })
  const [logoUploading, setLogoUploading] = useState(false)
  const [logoPreview, setLogoPreview] = useState<string | null>(organization?.logoUrl || null)

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // 미리보기 표시
    const reader = new FileReader()
    reader.onload = (e) => {
      setLogoPreview(e.target?.result as string)
    }
    reader.readAsDataURL(file)

    // 업로드
    setLogoUploading(true)
    try {
      const result = await adminApi.uploadImage(file, 'logo')
      setFormData({ ...formData, logoUrl: result.url })
    } catch (error) {
      console.error('Failed to upload logo:', error)
      alert('로고 업로드에 실패했습니다.')
      setLogoPreview(organization?.logoUrl || null)
    } finally {
      setLogoUploading(false)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (
      !organization
      && formData.organizationType === 'learning_center'
      && formData.lcOperationMode === 'TEST'
      && !window.confirm('TEST 센터에서는 교사가 원생 학습 기록을 자동 생성할 수 있습니다. TEST로 생성할까요?')
    ) return
    onSave(formData)
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900">
            {organization ? '기관 수정' : '새 기관 생성'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* 기본 정보 */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-700 border-b pb-2">기본 정보</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">기관명 *</label>
                <input
                  type="text"
                  className="input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="label">표시명</label>
                <input
                  type="text"
                  className="input"
                  value={formData.displayName || ''}
                  onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">대표자명</label>
                <input
                  type="text"
                  className="input"
                  value={formData.representativeName || ''}
                  onChange={(e) => setFormData({ ...formData, representativeName: e.target.value })}
                />
              </div>
              <div>
                <label className="label">기관 유형</label>
                <select
                  className="input"
                  value={formData.organizationType}
                  onChange={(e) => setFormData({ ...formData, organizationType: e.target.value })}
                >
                  {ORGANIZATION_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">업종</label>
                <input
                  type="text"
                  className="input"
                  value={formData.businessCategory || ''}
                  onChange={(e) => setFormData({ ...formData, businessCategory: e.target.value })}
                  placeholder="예: IT/소프트웨어"
                />
              </div>
              <div>
                <label className="label">서브도메인 *</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    className="input"
                    value={formData.subdomain}
                    onChange={(e) => setFormData({ ...formData, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    required
                    placeholder={formData.organizationType === 'learning_center' ? 'seoul101' : 'lgcns'}
                  />
                  <span className="text-sm text-slate-500 whitespace-nowrap">
                    {formData.organizationType === 'learning_center' ? '.quebonLC.tv' : '.aiqlab.tv'}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">언어 설정</label>
                <select
                  className="input"
                  value={formData.languageMode || 'ko'}
                  onChange={(e) => setFormData({ ...formData, languageMode: e.target.value })}
                >
                  {LANGUAGE_MODE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  B2B 앱에서 사용할 언어를 설정합니다
                </p>
              </div>
              {!organization && formData.organizationType === 'learning_center' && (
                <div>
                  <label className="label">LC 운영 모드</label>
                  <select
                    className="input"
                    value={formData.lcOperationMode || 'PRODUCTION'}
                    onChange={(event) => setFormData({ ...formData, lcOperationMode: event.target.value as 'PRODUCTION' | 'TEST' })}
                  >
                    <option value="PRODUCTION">PRODUCTION (기본)</option>
                    <option value="TEST">TEST — 자동완료 허용</option>
                  </select>
                  {formData.lcOperationMode === 'TEST' && (
                    <p className="mt-1 text-xs font-medium text-amber-700">테스트 데이터 생성 전용 센터입니다. 상용 전환 전 센터 전체 초기화가 필요합니다.</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* 연락처 */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-700 border-b pb-2">연락처</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">이메일</label>
                <input
                  type="email"
                  className="input"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </div>
              <div>
                <label className="label">전화번호</label>
                <input
                  type="tel"
                  className="input"
                  value={formData.phoneNumber || ''}
                  onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                />
              </div>
            </div>
            <div>
              <label className="label">웹사이트</label>
              <input
                type="url"
                className="input"
                value={formData.websiteUrl || ''}
                onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
                placeholder="https://"
              />
            </div>
          </div>

          {/* 브랜딩 */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-700 border-b pb-2">브랜딩</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">로고</label>
                <div className="flex items-center gap-4">
                  {/* 로고 미리보기 */}
                  <div className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden bg-slate-50">
                    {logoPreview ? (
                      <img src={logoPreview} alt="Logo preview" className="w-full h-full object-contain" />
                    ) : (
                      <Image className="w-6 h-6 text-slate-400" />
                    )}
                  </div>
                  {/* 업로드 버튼 */}
                  <div className="flex-1">
                    <label className="btn-secondary btn-sm inline-flex items-center gap-2 cursor-pointer">
                      {logoUploading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                      {logoUploading ? '업로드 중...' : '로고 업로드'}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/gif,image/svg+xml,image/webp"
                        className="hidden"
                        onChange={handleLogoUpload}
                        disabled={logoUploading}
                      />
                    </label>
                    <p className="text-xs text-slate-500 mt-1">PNG, JPG, SVG (최대 5MB)</p>
                  </div>
                </div>
              </div>
              <div>
                <label className="label">브랜드 색상</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    className="w-10 h-10 rounded border border-slate-300 cursor-pointer"
                    value={formData.primaryColor || '#3B82F6'}
                    onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                  />
                  <input
                    type="text"
                    className="input flex-1"
                    value={formData.primaryColor || ''}
                    onChange={(e) => setFormData({ ...formData, primaryColor: e.target.value })}
                    placeholder="#3B82F6"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 계약 정보 */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-700 border-b pb-2">계약 정보</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="label">계약 시작일</label>
                <input
                  type="date"
                  className="input"
                  value={formData.contractStartDate || ''}
                  onChange={(e) => setFormData({ ...formData, contractStartDate: e.target.value })}
                />
              </div>
              <div>
                <label className="label">계약 종료일</label>
                <input
                  type="date"
                  className="input"
                  value={formData.contractEndDate || ''}
                  onChange={(e) => setFormData({ ...formData, contractEndDate: e.target.value })}
                />
              </div>
              <div>
                <label className="label">최대 좌석수</label>
                <input
                  type="number"
                  className="input"
                  value={formData.maxSeats}
                  onChange={(e) => setFormData({ ...formData, maxSeats: parseInt(e.target.value) || 0 })}
                  min="0"
                />
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <button type="button" onClick={onClose} className="btn-secondary btn-md flex-1">
              취소
            </button>
            <button type="submit" disabled={isLoading} className="btn-primary btn-md flex-1">
              {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : organization ? '수정' : '생성'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  )
}


// ============================================================================
// Main Component
// ============================================================================

export function OrganizationManagement() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [keyword, setKeyword] = useState('')
  const [filterType, setFilterType] = useState<string>('')
  const [filterActive, setFilterActive] = useState<boolean | ''>('')
  const [sortField, setSortField] = useState<SortField>('name')
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc')
  const [formModal, setFormModal] = useState<AdminOrganization | null | undefined>(undefined)

  const { data: organizations = [], isLoading, error } = useQuery({
    queryKey: ['admin', 'organizations'],
    queryFn: adminApi.getOrganizations,
    staleTime: 30 * 1000
  })

  const createMutation = useMutation({
    mutationFn: adminApi.createOrganization,
    onSuccess: (newOrg) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] })
      setFormModal(undefined)
      alert(`"${newOrg.name}" 기관이 생성되었습니다.`)
    },
    onError: (error: Error) => {
      console.error('Create organization error:', error)
      if (import.meta.env.DEV) {
        alert(`기관 생성 실패:\n${error.message}`)
      } else {
        alert('기관 생성에 실패했습니다. 다시 시도해주세요.')
      }
    }
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateOrganizationRequest }) =>
      adminApi.updateOrganization(id, data),
    onSuccess: (updatedOrg) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] })
      setFormModal(undefined)
      alert(`"${updatedOrg.name}" 기관 정보가 수정되었습니다.`)
    },
    onError: (error: Error) => {
      console.error('Update organization error:', error)
      if (import.meta.env.DEV) {
        alert(`기관 수정 실패:\n${error.message}`)
      } else {
        alert('기관 수정에 실패했습니다. 다시 시도해주세요.')
      }
    }
  })

  // Filter and sort
  const filteredOrganizations = organizations
    .filter((org) => {
      const matchKeyword = !keyword ||
        org.name.toLowerCase().includes(keyword.toLowerCase()) ||
        (org.subdomain && org.subdomain.toLowerCase().includes(keyword.toLowerCase()))
      const matchType = !filterType || org.organizationType === filterType
      const matchActive = filterActive === '' || org.isActive === filterActive
      return matchKeyword && matchType && matchActive
    })
    .sort((a, b) => {
      let cmp = 0
      if (sortField === 'name') {
        cmp = a.name.localeCompare(b.name)
      } else if (sortField === 'createdAt') {
        cmp = (a.createdAt || '').localeCompare(b.createdAt || '')
      } else if (sortField === 'contractEndDate') {
        cmp = (a.contractEndDate || '').localeCompare(b.contractEndDate || '')
      }
      return sortOrder === 'asc' ? cmp : -cmp
    })

  const handleSave = (data: CreateOrganizationRequest | UpdateOrganizationRequest) => {
    if (formModal) {
      updateMutation.mutate({ id: formModal.id, data })
    } else {
      createMutation.mutate(data as CreateOrganizationRequest)
    }
  }

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('asc')
    }
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-10 h-10 text-slate-600 animate-spin mb-4" />
        <p className="text-slate-600">기관 목록을 불러오는 중...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-slate-900 font-medium mb-2">데이터를 불러올 수 없습니다</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Building2 className="w-7 h-7 text-blue-600" />
            기관 관리
          </h1>
          <p className="text-slate-600 mt-1">총 {filteredOrganizations.length}개의 기관</p>
        </div>
        <button
          onClick={() => setFormModal(null)}
          className="btn-primary btn-md inline-flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          새 기관
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="기관명, 서브도메인 검색..."
                className="input pl-10"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
            </div>
          </div>
          <select
            className="input w-32"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="">모든 유형</option>
            {ORGANIZATION_TYPES.map((type) => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
          <select
            className="input w-32"
            value={filterActive === '' ? '' : filterActive.toString()}
            onChange={(e) => setFilterActive(e.target.value === '' ? '' : e.target.value === 'true')}
          >
            <option value="">모든 상태</option>
            <option value="true">활성</option>
            <option value="false">비활성</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">ID</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">
                  <button onClick={() => toggleSort('name')} className="flex items-center gap-1 hover:text-slate-900">
                    기관명
                    <ArrowUpDown className="w-4 h-4" />
                  </button>
                </th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">모드</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">유형</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">
                  도메인
                </th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">
                  <button onClick={() => toggleSort('contractEndDate')} className="flex items-center gap-1 hover:text-slate-900">
                    계약 종료
                    <ArrowUpDown className="w-4 h-4" />
                  </button>
                </th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">좌석</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">상태</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredOrganizations.map((org) => (
                <tr key={org.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 text-sm text-slate-600">{org.id}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {org.logoUrl ? (
                        <img src={org.logoUrl} alt="" className="h-8 w-auto max-w-24 rounded object-contain" />
                      ) : (
                        <div
                          className="w-8 h-8 rounded flex items-center justify-center"
                          style={{ backgroundColor: org.primaryColor || '#3B82F6' }}
                        >
                          <Building2 className="w-4 h-4 text-white" />
                        </div>
                      )}
                      <span className="font-medium text-slate-900">{org.name}</span>
                      <button
                        onClick={() => navigate(`/organizations/${org.id}`)}
                        className="p-1 hover:bg-green-100 rounded text-green-500 hover:text-green-700"
                        title="상세 보기"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge variant={org.lcOperationMode === 'TEST' ? 'default' : 'secondary'}>
                      {org.lcOperationMode === 'TEST' ? 'TEST' : 'PRD'}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      {ORGANIZATION_TYPE_LABELS[org.organizationType] || org.organizationType}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-700">
                    {org.subdomain ? (
                      <code className="bg-slate-100 px-2 py-0.5 rounded">
                        {getFullDomain(org.subdomain, org.organizationType)}
                      </code>
                    ) : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-700">
                    {org.contractEndDate || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-700">
                    {org.currentSeats} / {org.maxSeats || '∞'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      org.isActive ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {org.isActive ? '활성' : '비활성'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredOrganizations.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            조건에 맞는 기관이 없습니다.
          </div>
        )}
      </div>

      {/* Form Modal */}
      {formModal !== undefined && (
        <OrganizationFormModal
          organization={formModal}
          onClose={() => setFormModal(undefined)}
          onSave={handleSave}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      )}

    </div>
  )
}
